import { Link, useLocation, useParams } from "react-router-dom";
import {
  BlogBlock,
  BlogTextSegment,
  formatBlogDate,
  getModifiedAt,
  getPostForLang,
} from "../../content/blogPosts";
import NotFound from "../NotFound";
import { ORGANIZATION_ID, SOFTWARE_ID } from "../../util/siteSchema";
import Seo from "../Seo";
import NewsletterForm from "../newsletter/NewsletterForm";
import BlogCta from "./BlogCta";
import RelatedPosts from "./RelatedPosts";
import styles from "./blog.module.css";

const BASE_URL = "https://shifttracker.net";
const OG_IMAGE_URL = `${BASE_URL}/og-image.jpg`;
const LOGO_URL = `${BASE_URL}/logo.png`;


const UI = {
  tr: {
    back: "← Tüm yazılar",
    author: "Shift Tracker ekibi",
    published: "Yayın",
    updated: "Güncelleme",
    reviewedBy: "İnceleyen",
    sources: "Kaynaklar",
  },
  en: {
    back: "← All articles",
    author: "The Shift Tracker team",
    published: "Published",
    updated: "Updated",
    reviewedBy: "Reviewed by",
    sources: "Sources",
  },
};

/** Plain text and internal links of a paragraph or callout. */
function renderSegments(segments: BlogTextSegment[]): JSX.Element[] {
  return segments.map((segment, i) =>
    segment.to ? (
      <Link key={i} to={segment.to} className={styles.articleLink}>
        {segment.text}
      </Link>
    ) : (
      <span key={i}>{segment.text}</span>
    )
  );
}

const CALLOUT_CLASS: Record<NonNullable<BlogBlock["tone"]>, string> = {
  answer: styles.calloutAnswer,
  example: styles.calloutExample,
  limitation: styles.calloutLimitation,
  product: styles.calloutProduct,
};

function renderBlock(block: BlogBlock, index: number): JSX.Element | null {
  switch (block.type) {
    case "heading":
      return (
        <h2 key={index} className={styles.articleHeading}>
          {block.text}
        </h2>
      );
    case "paragraph":
      if (block.segments) {
        return (
          <p key={index} className={styles.articleParagraph}>
            {renderSegments(block.segments)}
          </p>
        );
      }
      return (
        <p key={index} className={styles.articleParagraph}>
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul key={index} className={styles.articleList}>
          {(block.items ?? []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case "orderedList":
      return (
        <ol key={index} className={styles.articleList}>
          {(block.items ?? []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    case "table":
      if (!block.table) return null;
      return (
        <div key={index} className={styles.tableWrap}>
          <table className={styles.articleTable}>
            <caption>{block.table.caption}</caption>
            <thead>
              <tr>
                {block.table.headers.map((header, i) => (
                  <th key={i} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "callout":
      return (
        <aside
          key={index}
          className={`${styles.callout} ${block.tone ? CALLOUT_CLASS[block.tone] : ""}`}
        >
          {block.title && <p className={styles.calloutTitle}>{block.title}</p>}
          <p className={styles.calloutText}>
            {block.segments ? renderSegments(block.segments) : block.text}
          </p>
        </aside>
      );
    default:
      return null;
  }
}

function BlogPost(): JSX.Element {
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const isEnglish = location.pathname.startsWith("/en/");
  const lang: "tr" | "en" = isEnglish ? "en" : "tr";
  const prefix = isEnglish ? "/en" : "";
  const ui = UI[lang];

  const post = getPostForLang(slug, isEnglish);
  if (!post) {
    // The host already answered this URL with HTTP 404 (build/404.html).
    return <NotFound />;
  }

  const content = post[lang];
  const path = `/blog/${post.slug}`; // Turkish path
  const enPath = `/blog/${post.enSlug}`; // English path
  const canonical = isEnglish
    ? `${BASE_URL}/en${enPath}/`
    : `${BASE_URL}${path}/`;

  const modifiedAt = getModifiedAt(post, lang);
  const organization = {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Shift Tracker",
    url: `${BASE_URL}/`,
    logo: { "@type": "ImageObject", url: LOGO_URL },
  };
  // Dates match the visible byline: publication, and the last genuine content
  // change of this language version (falls back to publication).
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${canonical}#article`,
    headline: content.title,
    description: content.description,
    datePublished: post.publishedAt,
    dateModified: modifiedAt,
    inLanguage: lang,
    image: OG_IMAGE_URL,
    author: organization,
    publisher: organization,
    ...(post.reviewedBy
      ? { reviewedBy: { "@type": "Person", name: post.reviewedBy } }
      : {}),
    ...(content.sources?.length
      ? { citation: content.sources.map((source) => source.url) }
      : {}),
    about: { "@id": SOFTWARE_ID },
    mainEntityOfPage: canonical,
  };

  return (
    <div className={styles.page}>
      <Seo
        path={path}
        enPath={enPath}
        title={content.seoTitle ?? `${content.title} - Shift Tracker`}
        description={content.description}
        type="article"
        schema={schema}
      />
      <div className={styles.container}>
        <Link className={styles.back} to={`${prefix}/blog/`}>
          {ui.back}
        </Link>
        <article className={styles.article}>
          <h1 className={styles.articleTitle}>{content.title}</h1>
          <p className={styles.byline}>
            <span>{ui.author}</span>
            <span>
              {ui.published}:{" "}
              <time dateTime={post.publishedAt}>
                {formatBlogDate(post.publishedAt, lang)}
              </time>
            </span>
            {modifiedAt !== post.publishedAt && (
              <span>
                {ui.updated}:{" "}
                <time dateTime={modifiedAt}>{formatBlogDate(modifiedAt, lang)}</time>
              </span>
            )}
            {post.reviewedBy && (
              <span>
                {ui.reviewedBy}: {post.reviewedBy}
              </span>
            )}
          </p>
          {content.body.map((block, index) => renderBlock(block, index))}
          {content.sources && content.sources.length > 0 && (
            <section className={styles.sources}>
              <h2 className={styles.articleHeading}>{ui.sources}</h2>
              <ul className={styles.articleList}>
                {content.sources.map((source) => (
                  <li key={source.url}>
                    {source.url.startsWith("/") ? (
                      <Link to={source.url} className={styles.articleLink}>
                        {source.label}
                      </Link>
                    ) : (
                      <a
                        href={source.url}
                        className={styles.articleLink}
                        rel="noopener noreferrer"
                      >
                        {source.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>
        <BlogCta lang={lang} source={`blog:${post.slug}`} />
        <RelatedPosts currentSlug={post.slug} lang={lang} />
        <NewsletterForm source={`blog:${post.slug}`} />
      </div>
    </div>
  );
}

export default BlogPost;
